import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { generateId } from '../utils';

// 模板类型
export type TemplateType = 
  | 'edu-course' 
  | 'product-showcase' 
  | 'event-promotion' 
  | 'company-intro' 
  | 'resource-download';

// 模板分类
export type TemplateCategory = 'education' | 'product' | 'event' | 'company' | 'resource' | 'all';

// 模板定义
export interface Template {
  id: TemplateType;
  name: string;
  description: string;
  icon: string;
  category: TemplateCategory;
  thumbnail?: string;
  defaultConfig: TemplateConfig;
  sampleData: Record<string, unknown>;
}

// 模板配置
export interface TemplateConfig {
  title: string;
  subtitle: string;
  primaryColor: string;
  secondaryColor: string;
  sections: TemplateSection[];
}

// 模板区块
export interface TemplateSection {
  id: string;
  type: 'hero' | 'features' | 'content' | 'cta' | 'footer' | 'header';
  title?: string;
  content?: string;
  items?: Array<{
    title: string;
    description: string;
    icon?: string;
  }>;
}

// 已发布页面
export interface PublishedPage {
  id: string;
  title: string;
  path: string;
  templateId: TemplateType;
  html: string;
  publishedAt: number;
  updatedAt: number;
  url: string;
}

// 模板状态
export interface TemplateState {
  // 当前选中的模板
  currentTemplateId: TemplateType;
  
  // 已发布页面列表
  publishedPages: PublishedPage[];
  
  // 模板应用历史
  templateHistory: Array<{
    id: string;
    templateId: TemplateType;
    timestamp: number;
    designId?: string;
  }>;
  
  // Actions
  setCurrentTemplate: (templateId: TemplateType) => void;
  applyTemplate: (templateId: TemplateType, designId?: string) => void;
  
  // 发布相关
  addPublishedPage: (page: Omit<PublishedPage, 'id' | 'publishedAt' | 'updatedAt'>) => void;
  updatePublishedPage: (id: string, updates: Partial<PublishedPage>) => void;
  deletePublishedPage: (id: string) => void;
  getPublishedPageById: (id: string) => PublishedPage | undefined;
  getPublishedPageByPath: (path: string) => PublishedPage | undefined;
  
  // 获取模板
  getCurrentTemplate: () => Template;
  getTemplateById: (id: TemplateType) => Template;
}

// 5种模板定义
export const TEMPLATES: Record<TemplateType, Template> = {
  'edu-course': {
    id: 'edu-course',
    name: '教育课程',
    description: '适用于在线课程、培训项目介绍',
    icon: 'graduation-cap',
    category: 'education',
    thumbnail: '/templates/edu-course-thumb.jpg',
    defaultConfig: {
      title: '课程标题',
      subtitle: '课程副标题描述',
      primaryColor: '#2563eb',
      secondaryColor: '#9333ea',
      sections: [
        {
          id: 'hero',
          type: 'hero',
          title: '课程标题',
          content: '课程副标题描述',
        },
        {
          id: 'features',
          type: 'features',
          title: '课程大纲',
          items: [
            { title: '第一章', description: '章节内容描述', icon: '01' },
            { title: '第二章', description: '章节内容描述', icon: '02' },
            { title: '第三章', description: '章节内容描述', icon: '03' },
          ],
        },
        {
          id: 'cta',
          type: 'cta',
          title: '立即报名',
          content: '开启你的学习之旅',
        },
      ],
    },
    sampleData: {
      courseName: 'Python编程入门',
      instructor: '资深讲师',
      duration: '12周',
      level: '初级',
    },
  },
  'product-showcase': {
    id: 'product-showcase',
    name: '产品展示',
    description: '适用于SaaS产品、工具功能展示',
    icon: 'box-open',
    category: 'product',
    thumbnail: '/templates/product-showcase-thumb.jpg',
    defaultConfig: {
      title: '产品名称',
      subtitle: '产品核心价值主张',
      primaryColor: '#4f46e5',
      secondaryColor: '#06b6d4',
      sections: [
        {
          id: 'hero',
          type: 'hero',
          title: '产品名称',
          content: '产品核心价值主张',
        },
        {
          id: 'features',
          type: 'features',
          title: '核心功能',
          items: [
            { title: '功能一', description: '功能描述...', icon: '⚡' },
            { title: '功能二', description: '功能描述...', icon: '🛡️' },
            { title: '功能三', description: '功能描述...', icon: '📊' },
          ],
        },
        {
          id: 'cta',
          type: 'cta',
          title: '免费试用',
          content: '立即开始，无需信用卡',
        },
      ],
    },
    sampleData: {
      productName: '智能产品',
      tagline: '提升效率的终极解决方案',
      pricing: '¥99/月',
      features: ['功能一', '功能二', '功能三'],
    },
  },
  'event-promotion': {
    id: 'event-promotion',
    name: '活动宣传',
    description: '适用于线下活动、线上讲座宣传',
    icon: 'calendar-alt',
    category: 'event',
    thumbnail: '/templates/event-promotion-thumb.jpg',
    defaultConfig: {
      title: '活动名称',
      subtitle: '活动副标题',
      primaryColor: '#f97316',
      secondaryColor: '#ec4899',
      sections: [
        {
          id: 'hero',
          type: 'hero',
          title: '活动名称',
          content: '活动副标题',
        },
        {
          id: 'features',
          type: 'features',
          title: '活动信息',
          items: [
            { title: '活动时间', description: '待定', icon: '📅' },
            { title: '活动地点', description: '待定', icon: '📍' },
            { title: '参与人数', description: '待定', icon: '👥' },
          ],
        },
        {
          id: 'cta',
          type: 'cta',
          title: '立即报名',
          content: '名额有限，先到先得',
        },
      ],
    },
    sampleData: {
      eventName: '精彩活动',
      eventType: '线下活动',
      date: '待定',
      location: '待定',
    },
  },
  'company-intro': {
    id: 'company-intro',
    name: '企业介绍',
    description: '适用于公司官网、团队介绍',
    icon: 'building',
    category: 'company',
    thumbnail: '/templates/company-intro-thumb.jpg',
    defaultConfig: {
      title: '公司名称',
      subtitle: '企业愿景和使命描述',
      primaryColor: '#111827',
      secondaryColor: '#6b7280',
      sections: [
        {
          id: 'hero',
          type: 'hero',
          title: '公司名称',
          content: '企业愿景和使命描述',
        },
        {
          id: 'content',
          type: 'content',
          title: '关于我们',
          content: '公司简介内容...',
        },
        {
          id: 'features',
          type: 'features',
          title: '我们的优势',
          items: [
            { title: '专业团队', description: '经验丰富的专业团队' },
            { title: '优质服务', description: '客户至上的服务理念' },
            { title: '创新技术', description: '持续创新的技术能力' },
          ],
        },
      ],
    },
    sampleData: {
      companyName: '公司名称',
      founded: '2020年',
      employees: '100+',
      location: '北京',
    },
  },
  'resource-download': {
    id: 'resource-download',
    name: '资源下载',
    description: '适用于资料分享、工具下载',
    icon: 'download',
    category: 'resource',
    thumbnail: '/templates/resource-download-thumb.jpg',
    defaultConfig: {
      title: '资源中心',
      subtitle: '发现优质资源',
      primaryColor: '#2563eb',
      secondaryColor: '#9333ea',
      sections: [
        {
          id: 'hero',
          type: 'hero',
          title: '资源中心',
          content: '发现优质资源',
        },
        {
          id: 'features',
          type: 'features',
          title: '热门资源',
          items: [
            { title: '资源名称', description: '资源描述...', icon: '📄' },
            { title: '资源名称', description: '资源描述...', icon: '📄' },
            { title: '资源名称', description: '资源描述...', icon: '📄' },
          ],
        },
      ],
    },
    sampleData: {
      resourceName: '资源名称',
      fileType: 'PDF',
      fileSize: '2.5 MB',
      downloads: 1000,
    },
  },
};

// 生成模板HTML
export function generateTemplateHTML(templateId: TemplateType, config?: Partial<TemplateConfig>): string {
  const template = TEMPLATES[templateId];
  const finalConfig = { ...template.defaultConfig, ...config };
  
  const generators: Record<TemplateType, () => string> = {
    'edu-course': () => generateEduCourseHTML(finalConfig),
    'product-showcase': () => generateProductShowcaseHTML(finalConfig),
    'event-promotion': () => generateEventPromotionHTML(finalConfig),
    'company-intro': () => generateCompanyIntroHTML(finalConfig),
    'resource-download': () => generateResourceDownloadHTML(finalConfig),
  };
  
  return generators[templateId]();
}

// 教育课程模板HTML
function generateEduCourseHTML(config: TemplateConfig): string {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gray-50">
    <section class="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div class="container mx-auto px-4 text-center">
            <h1 class="text-4xl md:text-5xl font-bold mb-4">${config.title}</h1>
            <p class="text-xl mb-8">${config.subtitle}</p>
            <button class="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition">立即报名</button>
        </div>
    </section>
    <section class="py-16 container mx-auto px-4">
        <h2 class="text-3xl font-bold text-center mb-12">课程大纲</h2>
        <div class="grid md:grid-cols-3 gap-8">
            ${config.sections.find(s => s.type === 'features')?.items?.map((item, i) => `
            <div class="bg-white p-6 rounded-lg shadow">
                <div class="w-12 h-12 bg-${['blue', 'purple', 'pink'][i]}-100 rounded-full flex items-center justify-center mb-4">
                    <span class="text-${['blue', 'purple', 'pink'][i]}-600 font-bold">${item.icon}</span>
                </div>
                <h3 class="font-semibold mb-2">${item.title}</h3>
                <p class="text-gray-600">${item.description}</p>
            </div>
            `).join('') || ''}
        </div>
    </section>
</body>
</html>`;
}

// 产品展示模板HTML
function generateProductShowcaseHTML(config: TemplateConfig): string {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-white">
    <section class="bg-gray-900 text-white py-20">
        <div class="container mx-auto px-4 text-center">
            <h1 class="text-4xl md:text-6xl font-bold mb-6">${config.title}</h1>
            <p class="text-xl text-gray-300 mb-8">${config.subtitle}</p>
            <button class="bg-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition">免费试用</button>
        </div>
    </section>
    <section class="py-20 container mx-auto px-4">
        <h2 class="text-3xl font-bold text-center mb-16">核心功能</h2>
        <div class="grid md:grid-cols-3 gap-12">
            ${config.sections.find(s => s.type === 'features')?.items?.map((item, i) => `
            <div class="text-center">
                <div class="w-16 h-16 bg-${['blue', 'green', 'purple'][i]}-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <span class="text-2xl">${item.icon}</span>
                </div>
                <h3 class="font-semibold mb-2">${item.title}</h3>
                <p class="text-gray-600">${item.description}</p>
            </div>
            `).join('') || ''}
        </div>
    </section>
</body>
</html>`;
}

// 活动宣传模板HTML
function generateEventPromotionHTML(config: TemplateConfig): string {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gradient-to-br from-orange-400 to-pink-500 min-h-screen">
    <div class="container mx-auto px-4 py-20">
        <div class="bg-white rounded-3xl shadow-2xl overflow-hidden max-w-4xl mx-auto">
            <div class="bg-gradient-to-r from-orange-500 to-pink-500 text-white p-12 text-center">
                <span class="inline-block bg-white/20 px-4 py-1 rounded-full text-sm mb-4">活动</span>
                <h1 class="text-4xl md:text-5xl font-bold mb-4">${config.title}</h1>
                <p class="text-xl opacity-90">${config.subtitle}</p>
            </div>
            <div class="p-12">
                <div class="grid md:grid-cols-3 gap-8 mb-12">
                    ${config.sections.find(s => s.type === 'features')?.items?.map((item, i) => `
                    <div class="text-center">
                        <div class="w-12 h-12 bg-${['orange', 'pink', 'purple'][i]}-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <span class="text-${['orange', 'pink', 'purple'][i]}-600">${item.icon}</span>
                        </div>
                        <div class="font-semibold">${item.title}</div>
                        <div class="text-gray-600">${item.description}</div>
                    </div>
                    `).join('') || ''}
                </div>
                <div class="text-center">
                    <button class="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-12 py-4 rounded-full text-lg font-semibold hover:shadow-lg transition">立即报名</button>
                </div>
            </div>
        </div>
    </div>
</body>
</html>`;
}

// 企业介绍模板HTML
function generateCompanyIntroHTML(config: TemplateConfig): string {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gray-50">
    <section class="bg-gray-900 text-white py-24">
        <div class="container mx-auto px-4 text-center">
            <h1 class="text-4xl md:text-6xl font-bold mb-6">${config.title}</h1>
            <p class="text-xl text-gray-400 max-w-2xl mx-auto">${config.subtitle}</p>
        </div>
    </section>
    <section class="py-20 container mx-auto px-4">
        <div class="max-w-3xl mx-auto text-center">
            <h2 class="text-3xl font-bold mb-8">关于我们</h2>
            <p class="text-gray-600 text-lg leading-relaxed">${config.sections.find(s => s.type === 'content')?.content || '公司简介内容...'}</p>
        </div>
    </section>
</body>
</html>`;
}

// 资源下载模板HTML
function generateResourceDownloadHTML(config: TemplateConfig): string {
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${config.title}</title>
    <script src="https://cdn.tailwindcss.com"></script>
</head>
<body class="bg-gray-50">
    <header class="bg-white shadow-sm">
        <div class="container mx-auto px-4 py-6">
            <h1 class="text-2xl font-bold text-gray-800">${config.title}</h1>
        </div>
    </header>
    <section class="bg-gradient-to-r from-blue-600 to-purple-600 py-12">
        <div class="container mx-auto px-4 text-center">
            <h2 class="text-3xl font-bold text-white mb-4">${config.subtitle}</h2>
        </div>
    </section>
    <section class="py-12 container mx-auto px-4">
        <div class="grid md:grid-cols-3 gap-6">
            ${config.sections.find(s => s.type === 'features')?.items?.map((item) => `
            <div class="bg-white rounded-lg shadow p-6">
                <div class="flex items-center justify-between mb-4">
                    <span class="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm">文档</span>
                    <span class="text-gray-400 text-sm">2.5 MB</span>
                </div>
                <h3 class="font-semibold mb-2">${item.title}</h3>
                <button class="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition">下载</button>
            </div>
            `).join('') || ''}
        </div>
    </section>
</body>
</html>`;
}

// 创建模板Store
export const useTemplateStore = create<TemplateState>()(
  persist(
    (set, get) => ({
      // 初始状态
      currentTemplateId: 'edu-course',
      publishedPages: [],
      templateHistory: [],

      // 设置当前模板
      setCurrentTemplate: (templateId) => {
        set({ currentTemplateId: templateId });
      },

      // 应用模板
      applyTemplate: (templateId, designId) => {
        const historyItem = {
          id: generateId(),
          templateId,
          timestamp: Date.now(),
          designId,
        };
        
        set((state) => ({
          currentTemplateId: templateId,
          templateHistory: [historyItem, ...state.templateHistory].slice(0, 20),
        }));
      },

      // 添加已发布页面
      addPublishedPage: (page) => {
        const newPage: PublishedPage = {
          ...page,
          id: generateId(),
          publishedAt: Date.now(),
          updatedAt: Date.now(),
        };
        
        set((state) => ({
          publishedPages: [...state.publishedPages, newPage],
        }));
      },

      // 更新已发布页面
      updatePublishedPage: (id, updates) => {
        set((state) => ({
          publishedPages: state.publishedPages.map((p) =>
            p.id === id 
              ? { ...p, ...updates, updatedAt: Date.now() } 
              : p
          ),
        }));
      },

      // 删除已发布页面
      deletePublishedPage: (id) => {
        set((state) => ({
          publishedPages: state.publishedPages.filter((p) => p.id !== id),
        }));
      },

      // 通过ID获取已发布页面
      getPublishedPageById: (id) => {
        return get().publishedPages.find((p) => p.id === id);
      },

      // 通过路径获取已发布页面
      getPublishedPageByPath: (path) => {
        return get().publishedPages.find((p) => p.path === path);
      },

      // 获取当前模板
      getCurrentTemplate: () => {
        return TEMPLATES[get().currentTemplateId];
      },

      // 通过ID获取模板
      getTemplateById: (id) => {
        return TEMPLATES[id];
      },
    }),
    {
      name: 'template-storage-v2',
      partialize: (state) => ({
        currentTemplateId: state.currentTemplateId,
        publishedPages: state.publishedPages,
        templateHistory: state.templateHistory,
      }),
    }
  )
);

// 导出所有模板列表
export const getAllTemplates = (): Template[] => Object.values(TEMPLATES);

// 按分类获取模板
export const getTemplatesByCategory = (category: TemplateCategory): Template[] => {
  if (category === 'all') return getAllTemplates();
  return Object.values(TEMPLATES).filter((t) => t.category === category);
};